import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const ATTEMPTED_REFRESH_VERSION_KEY = "formly_update_attempted_version";
const DISMISSED_VERSION_KEY = "formly_update_dismissed_version";

type VersionCheckSource =
  | "initial"
  | "poll"
  | "focus"
  | "visibility"
  | "chunk_error"
  | "refresh";

const getSessionStorageItem = (key: string): string => {
  try {
    return window.sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
};

const setSessionStorageItem = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Best-effort only: inability to read/write storage should not break runtime checks.
  }
};

const getRefreshUrl = () => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set("_fv", Date.now().toString());
  return currentUrl.toString();
};

const getErrorMessage = (value: unknown): string => {
  if (value instanceof Error) {
    return value.message;
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object" && value !== null) {
    const message = (value as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }

  return "";
};

const isChunkMismatchError = (value: unknown): boolean => {
  const message = getErrorMessage(value).trim().toLowerCase();
  if (!message) {
    return false;
  }

  return (
    message.includes("chunkloaderror") ||
    message.includes("loading chunk") ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed")
  );
};

export const useAppVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const localVersionRef = useRef((window._env_.APP_VERSION ?? "").trim());
  const isMountedRef = useRef(true);
  const checkInFlightRef = useRef(false);
  const remoteVersionRef = useRef("");

  const trackVersionCheck = useCallback(
    ({
      action,
      source,
      remoteVersion,
      attemptedVersion,
      dismissedVersion,
    }: {
      action: string;
      source: VersionCheckSource;
      remoteVersion: string;
      attemptedVersion?: string;
      dismissedVersion?: string;
    }) => {
      Sentry.addBreadcrumb({
        category: "app-version-check",
        level: "info",
        message: action,
        data: {
          action,
          source,
          localVersion: localVersionRef.current,
          remoteVersion,
          attemptedRefreshVersion:
            attemptedVersion ??
            getSessionStorageItem(ATTEMPTED_REFRESH_VERSION_KEY),
          dismissedVersion:
            dismissedVersion ?? getSessionStorageItem(DISMISSED_VERSION_KEY),
        },
      });
    },
    []
  );

  const refreshNow = useCallback(() => {
    const remoteVersion = remoteVersionRef.current;

    if (remoteVersion) {
      setSessionStorageItem(ATTEMPTED_REFRESH_VERSION_KEY, remoteVersion);
    }
    if (isMountedRef.current) {
      setUpdateAvailable(false);
    }

    trackVersionCheck({
      action: "refresh_clicked",
      source: "refresh",
      remoteVersion,
      attemptedVersion: remoteVersion || undefined,
    });
    window.location.replace(getRefreshUrl());
  }, [trackVersionCheck]);

  const checkForVersionUpdate = useCallback(
    async (source: VersionCheckSource) => {
      if (checkInFlightRef.current) {
        return;
      }

      checkInFlightRef.current = true;

      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as { appVersion?: string };
        const remoteVersion =
          typeof data.appVersion === "string" ? data.appVersion.trim() : "";
        const localVersion = localVersionRef.current;
        const isMismatch = !!remoteVersion && remoteVersion !== localVersion;
        const attemptedVersion = getSessionStorageItem(
          ATTEMPTED_REFRESH_VERSION_KEY
        );
        const dismissedVersion = getSessionStorageItem(DISMISSED_VERSION_KEY);

        remoteVersionRef.current = remoteVersion;

        if (!isMismatch) {
          if (isMountedRef.current) {
            setUpdateAvailable(false);
          }
          trackVersionCheck({
            action: "no_mismatch",
            source,
            remoteVersion,
            attemptedVersion,
            dismissedVersion,
          });
          return;
        }

        if (dismissedVersion === remoteVersion) {
          if (isMountedRef.current) {
            setUpdateAvailable(false);
          }
          trackVersionCheck({
            action: "suppress_same_version",
            source,
            remoteVersion,
            attemptedVersion,
            dismissedVersion,
          });
          return;
        }

        if (attemptedVersion === remoteVersion) {
          setSessionStorageItem(DISMISSED_VERSION_KEY, remoteVersion);
          if (isMountedRef.current) {
            setUpdateAvailable(false);
          }
          trackVersionCheck({
            action: "dismiss_after_attempt",
            source,
            remoteVersion,
            attemptedVersion,
            dismissedVersion: remoteVersion,
          });
          return;
        }

        if (!isMountedRef.current) {
          return;
        }

        setUpdateAvailable(true);
        trackVersionCheck({
          action: "show",
          source,
          remoteVersion,
          attemptedVersion,
          dismissedVersion,
        });
      } catch {
        // Best-effort check: ignore network/runtime errors and retry on next cycle.
      } finally {
        checkInFlightRef.current = false;
      }
    },
    [trackVersionCheck]
  );

  useEffect(() => {
    isMountedRef.current = true;

    void checkForVersionUpdate("initial");

    const intervalId = window.setInterval(
      () => void checkForVersionUpdate("poll"),
      VERSION_CHECK_INTERVAL_MS
    );

    const handleFocus = () => void checkForVersionUpdate("focus");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForVersionUpdate("visibility");
      }
    };

    const handleVitePreloadError = (event: Event) => {
      event.preventDefault();
      void checkForVersionUpdate("chunk_error");
    };

    const handleError = (event: ErrorEvent) => {
      if (!isChunkMismatchError(event.error ?? event.message)) {
        return;
      }

      event.preventDefault();
      void checkForVersionUpdate("chunk_error");
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkMismatchError(event.reason)) {
        return;
      }

      event.preventDefault();
      void checkForVersionUpdate("chunk_error");
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("vite:preloadError", handleVitePreloadError);
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      isMountedRef.current = false;

      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("vite:preloadError", handleVitePreloadError);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [checkForVersionUpdate]);

  return {
    updateAvailable,
    refreshNow,
  };
};
